import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { getPageStaticProps } from 'lib/dato-request';
import Head from 'next/head';
import { Layout } from '@/components/Layout';
import CheckoutIntro from '@/components/Checkout/Onboarding/CheckoutIntro';
import CheckoutChild1 from '@/components/Checkout/CheckoutChild/CheckoutChild1';
import CheckoutChild2 from '@/components/Checkout/CheckoutChild/CheckoutChild2';
import CheckoutChild3 from '@/components/Checkout/CheckoutChild/CheckoutChild3';
import CheckoutLoginReroute from '@/components/Checkout/CheckoutChild/CheckoutLoginReroute';
import ProgressBar from '@/components/Checkout/components/ProgressBar';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { BlockKlaviyo, useAnalytics } from '@/analytics/AnalyticsProvider';
import { UserTypeSelectionCanceled } from '@/analytics/events';

const Onboarding = ({ data }) => {
    const analytics = useAnalytics();
    const { page, site } = data;
    console.log("🚀 ~ Onboarding ~ page:", page)
    const { components, seo } = page;
    const router = useRouter();
    const { email, stage, token, client: clientSecretEncoded } = router.query ?? {};

    useEffect(() => {
        console.log('page loaded', analytics);
        // analytics.page('Onboarding');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // get the stage from the query string
    const activeTab = useMemo(() => parseInt((stage ?? '0') as string, 10), [stage]);
    const [isLoading, setLoading] = useState(false);

    const cancelOnboarding = () => {
        analytics.track(UserTypeSelectionCanceled, { email, token, stage }, BlockKlaviyo);

        // remove the checkout state from local storage
        localStorage.removeItem('checkout-state');
        // go back to home page
        router.push('/', undefined, { shallow: true });
    };

    const onBoardingElements = useMemo(() => {
        const nextStage = (args) => {
            // save the client secret
            const query = {
                ...router.query,
                ...(args ?? {})
            };
            // perform a shallow push
            return router.push({ pathname: '/onboarding', query }, undefined, { shallow: true, scroll: true });
        };

        return [
            <CheckoutIntro key='CheckoutIntro' isLoading={isLoading} setLoading={setLoading} nextStage={nextStage} />,
            <CheckoutChild1 key='CheckoutChild1' isLoading={isLoading} setLoading={setLoading} nextStage={nextStage} />,
            <CheckoutChild2 key='CheckoutChild2' isLoading={isLoading} setLoading={setLoading} nextStage={nextStage} token={token} />,
            <CheckoutChild3 key='CheckoutChild3' />,
            <CheckoutLoginReroute key='CheckoutLoginReroute' />
        ];
    }, [isLoading, router, token]);



    let progressBarWidth = 20;
    let backgroundClassName = 'bg-checkout-timestables-background';
    let mobileHeader = 'Let’s Get Started!';
    if (activeTab === 1) {
        progressBarWidth = 40;
    }
    if (activeTab === 2) {
        progressBarWidth = 77;
        backgroundClassName = 'bg-[#F7FAFC] sm:bg-checkout-timestables-background';
        mobileHeader = 'Okay, Great!';
    }
    if (activeTab === 3) {
        progressBarWidth = 100;
        backgroundClassName = 'bg-[#F7FAFC] sm:bg-checkout-timestables-background';
        mobileHeader = 'Email Sent!';
    }
    if (activeTab === 4) mobileHeader = '';

    return (
        <Layout hideNavbar={true} hideFooter={true}>
            <NextSeo title={seo?.title} description={seo?.description} />
            <Head>
                <meta name='keywords' content={page?.keywords} />
            </Head>
            <div className={`${backgroundClassName} bg-cover h-screen pt-5 py-0 sm:py-5 lg:px-8 overflow-y-auto relative`}>
                <div className='max-w-[1274px] mx-auto grow'>
                    <div className='block sm:hidden text-2xl font-semibold text-center text-[#303030] mb-8'>{mobileHeader}</div>
                    <LoadingOverlay show={isLoading} className='z-50 bg-red-300' style={{ background: '#00000060' }} />
                    <div>{onBoardingElements[activeTab]}</div>
                </div>
            </div>
        </Layout>
    );
};

export async function getStaticProps({ preview = false }) {
    return getPageStaticProps('Onboarding');
}

export default Onboarding;
